import { useCallback, useEffect, useState } from 'react'
import { digitsOnly, formatCpf } from '@/utils/cpf'
import type { CaptchaConfig } from '@/components/AleloCaptcha'
import { toTransactionErrorCode, type TransactionErrorLike } from '@/utils/transactionError'

type ScreenTexts = {
  title?: string
  passwordPlaceholder?: string
  buttonText?: string
  forgotPasswordText?: string
  editIdentifierText?: string
}

type LoginPasswordSdk = {
  login: (data: {
    username: string
    password: string
    captcha?: string
  }) => Promise<void>
  getErrors?: () => TransactionErrorLike[]
  screen?: {
    texts?: ScreenTexts
    resetPasswordLink?: string | null
    editIdentifierLink?: string | null
    data?: { username?: string } | null
    isCaptchaAvailable?: boolean
    captcha?: CaptchaConfig | null
    captchaProvider?: string | null
    captchaSiteKey?: string | null
    captchaImage?: string | null
  }
  transaction?: {
    isForgotPasswordEnabled?: boolean
    username?: string | null
  }
}

async function createLoginPasswordSdk(): Promise<LoginPasswordSdk | null> {
  try {
    const mod = await import('@auth0/auth0-acul-js/login-password')
    return new mod.default() as unknown as LoginPasswordSdk
  } catch {
    return null
  }
}

function resolveCaptcha(sdk: LoginPasswordSdk | null): CaptchaConfig | null {
  if (!sdk?.screen) return null
  const fromObj = sdk.screen.captcha
  if (fromObj?.provider) {
    return {
      provider: fromObj.provider,
      siteKey: fromObj.siteKey ?? sdk.screen.captchaSiteKey,
      image: fromObj.image ?? sdk.screen.captchaImage,
    }
  }
  if (sdk.screen.isCaptchaAvailable || sdk.screen.captchaProvider) {
    return {
      provider: sdk.screen.captchaProvider,
      siteKey: sdk.screen.captchaSiteKey,
      image: sdk.screen.captchaImage,
    }
  }
  return null
}

/**
 * Pós Identifier First: senha + username já resolvido pelo Auth0.
 */
export function useLoginPasswordManager() {
  const [error, setError] = useState<string>()
  const [sdk, setSdk] = useState<LoginPasswordSdk | null>(null)
  const [captchaToken, setCaptchaToken] = useState<string>()

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const instance = await createLoginPasswordSdk()
      if (!cancelled) setSdk(instance)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const resolvedUsername =
    sdk?.screen?.data?.username ?? sdk?.transaction?.username ?? ''

  const captchaConfig = resolveCaptcha(sdk)
  const captchaRequired = Boolean(captchaConfig?.provider)
  const onCaptchaToken = useCallback((token: string | undefined) => {
    setCaptchaToken(token)
  }, [])

  async function handleLogin(password: string, usernameOverride?: string) {
    setError(undefined)
    const raw = usernameOverride || resolvedUsername
    const username = digitsOnly(raw) || raw.trim()
    if (!username) {
      setError('USERNAME_MISSING')
      throw new Error('USERNAME_MISSING')
    }
    if (!password) {
      setError('PASSWORD_REQUIRED')
      throw new Error('PASSWORD_REQUIRED')
    }
    if (captchaRequired && !captchaToken) {
      setError('CAPTCHA_REQUIRED')
      throw new Error('CAPTCHA_REQUIRED')
    }

    const instance = sdk ?? (await createLoginPasswordSdk())
    if (instance?.login) {
      await instance.login({
        username,
        password,
        ...(captchaToken ? { captcha: captchaToken } : {}),
      })
      return
    }

    console.info('[preview login-password]', {
      username,
      passwordLength: password.length,
    })
    window.dispatchEvent(
      new CustomEvent('alelo:acul-preview', {
        detail: {
          screen: 'login-password',
          action: 'login',
          data: { username, password: '***' },
        },
      }),
    )
  }

  async function handleForgotPassword() {
    const instance = sdk ?? (await createLoginPasswordSdk())
    const link = instance?.screen?.resetPasswordLink
    if (link) {
      window.location.assign(link)
      return
    }
    window.location.search = '?screen=reset-password-request'
  }

  function handleEditIdentifier() {
    const link = sdk?.screen?.editIdentifierLink
    if (link) {
      window.location.assign(link)
      return
    }
    window.location.search = '?screen=login-id'
  }

  const displayIdentifier = resolvedUsername
    ? formatCpf(digitsOnly(resolvedUsername) || resolvedUsername)
    : ''

  // Senha errada volta no re-render do Auth0, não como exceção do login().
  const serverError = toTransactionErrorCode(sdk?.getErrors?.())

  return {
    handleLogin,
    handleForgotPassword,
    handleEditIdentifier,
    displayIdentifier,
    captchaConfig,
    captchaReady: !captchaRequired || Boolean(captchaToken),
    onCaptchaToken,
    texts: sdk?.screen?.texts,
    showForgot: sdk?.transaction?.isForgotPasswordEnabled !== false,
    error: error || serverError,
  }
}
